import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import debounce from "lodash/debounce";
import apiClient from "../../pages/keycloak/axiosInstance";
import { useKeycloak } from "@react-keycloak/web";

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────
const Icons = {
  Search:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  X:        () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Folder:   () => <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z" opacity="0.9"/></svg>,
  FolderOpen: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" opacity="0.9"/><path d="M2 10h20" stroke="white" strokeWidth="1.5" fill="none"/></svg>,
  Chevron:  ({ open }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.25s ease" }}>
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  Download: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Trash:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
  File:     () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  Warn:     () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Empty:    () => <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
};

// ─── File type helpers ────────────────────────────────────────────────────────
const getExt = (name = "") => name.split(".").pop().toLowerCase();
const extColor = (ext) => ({
  pdf:  "#E05C45", xlsx: "#2E9E6B", xls: "#2E9E6B", csv: "#2E9E6B",
  doc:  "#3A7BD5", docx: "#3A7BD5", jpg: "#D4860A", jpeg: "#D4860A",
  png:  "#D4860A", webp: "#D4860A",
}[ext] || "#7C6FAD");

const highlight = (text, q) => {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} style={{ background: "#FFE066", color: "#1a1a1a", borderRadius: 3, padding: "0 2px" }}>{p}</mark>
      : p
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=Instrument+Sans:wght@400;500;600&display=swap');

  .de-root *, .de-root *::before, .de-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .de-root {
    --bg:        white;
    --surface:   #FFFFFF;
    --border:    #E5E1D8;
    --text-1:    #1C1917;
    --text-2:    #6B6560;
    --text-3:    #A09A94;
    --accent-a:  #3461D1;
    --accent-b:  #1A9A78;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05);
    --shadow-md: 0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.05);
    --shadow-lg: 0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06);
    --radius:    20px;
    --font-head: 'Bricolage Grotesque', sans-serif;
    --font-body: 'Instrument Sans', sans-serif;

    background: var(--bg);
    color: var(--text-1);
    font-family: var(--font-body);
    min-height: 100vh;
    padding: 36px 28px 64px;
  }

  /* ── Page header ── */
  .de-header { margin-bottom: 32px; }
  .de-page-label {
    font-family: var(--font-body);
    font-size: 11px; font-weight: 600; letter-spacing: 3px;
    text-transform: uppercase; color: var(--text-3);
    margin-bottom: 8px; display: block;
  }
  .de-page-title {
    font-family: var(--font-head);
    font-size: clamp(24px, 3.5vw, 36px);
    font-weight: 700; color: var(--text-1);
    line-height: 1.1; letter-spacing: -0.5px;
  }

  /* ── Toolbar row ── */
  .de-toolbar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 28px; flex-wrap: wrap;
  }
  .de-search {
    position: relative; flex: 1; min-width: 220px; max-width: 400px;
  }
  .de-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--text-3); pointer-events: none; display: flex; }
  .de-search-input {
    width: 100%; padding: 10px 36px 10px 40px;
    background: var(--surface); border: 1.5px solid var(--border);
    border-radius: 10px; font-family: var(--font-body); font-size: 14px;
    color: var(--text-1); outline: none; transition: border-color 0.18s, box-shadow 0.18s;
    box-shadow: var(--shadow-sm);
  }
  .de-search-input::placeholder { color: var(--text-3); }
  .de-search-input:focus { border-color: var(--accent-a); box-shadow: 0 0 0 3px rgba(52,97,209,0.1); }
  .de-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-3); cursor: pointer; display: flex; padding: 3px; border-radius: 6px; transition: color 0.15s; }
  .de-search-clear:hover { color: var(--text-1); }

  .de-tabs { display: flex; gap: 4px; background: var(--surface); border: 1.5px solid var(--border); border-radius: 10px; padding: 3px; box-shadow: var(--shadow-sm); }
  .de-tab {
    padding: 7px 16px; border-radius: 7px; border: none; background: none;
    font-family: var(--font-body); font-size: 13px; font-weight: 500;
    color: var(--text-2); cursor: pointer; transition: all 0.17s; white-space: nowrap;
  }
  .de-tab.active { background: var(--text-1); color: #fff; }
  .de-tab:hover:not(.active) { background: var(--bg); color: var(--text-1); }

  .de-expand-btn {
    padding: 9px 14px; border-radius: 10px; border: 1.5px solid var(--border);
    background: var(--surface); color: var(--text-2); cursor: pointer;
    font-family: var(--font-body); font-size: 12px; font-weight: 500;
    transition: all 0.17s; box-shadow: var(--shadow-sm); white-space: nowrap;
  }
  .de-expand-btn:hover { border-color: var(--text-2); color: var(--text-1); }

  /* ── Stats pills ── */
  .de-stats { display: flex; gap: 8px; margin-bottom: 28px; flex-wrap: wrap; }
  .de-stat-pill {
    padding: 5px 14px; border-radius: 100px;
    font-size: 12px; font-weight: 500; font-family: var(--font-body);
    border: 1.5px solid; white-space: nowrap;
  }

  /* ── Section cluster ── */
  .de-section { margin-bottom: 36px; }
  .de-section-label {
    font-family: var(--font-head);
    font-size: 13px; font-weight: 600; letter-spacing: 0.3px;
    margin-bottom: 14px; display: flex; align-items: center; gap: 10px;
  }
  .de-section-line { flex: 1; height: 1.5px; border-radius: 2px; }

  /* ── Folder grid ── */
  .de-folder-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 12px;
    align-items: start;
  }

  /* ── Folder card ── */
  .de-folder {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .de-folder:hover { box-shadow: var(--shadow-md); }
  .de-folder.open { border-color: #D8D3CA; box-shadow: var(--shadow-md); }

  .de-folder-header {
    display: flex; align-items: center; gap: 10px;
    padding: 13px 15px; cursor: pointer;
    user-select: none; transition: background 0.15s;
  }
  .de-folder-header:hover { background: #FAFAF7; }
  .de-folder-icon { display: flex; flex-shrink: 0; }
  .de-folder-name {
    flex: 1; font-family: var(--font-head);
    font-size: 13.5px; font-weight: 600; color: var(--text-1);
    line-height: 1.3; word-break: break-word;
  }
  .de-folder-count {
    font-size: 11px; font-weight: 600; padding: 3px 8px;
    border-radius: 100px; flex-shrink: 0;
  }
  .de-folder-chevron { color: var(--text-3); flex-shrink: 0; display: flex; }

  /* ── File list ── */
  .de-file-list { border-top: 1.5px solid var(--border); }
  .de-file-row {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 15px; border-bottom: 1px solid #F3F0EB;
    transition: background 0.13s; cursor: pointer;
  }
  .de-file-row:last-child { border-bottom: none; }
  .de-file-row:hover { background: #F9F7F4; }
  .de-file-ext {
    font-family: var(--font-head); font-size: 9px; font-weight: 700;
    letter-spacing: 0.5px; text-transform: uppercase;
    padding: 2px 6px; border-radius: 5px; flex-shrink: 0;
  }
  .de-file-name {
    flex: 1; font-size: 12.5px; color: var(--text-1);
    line-height: 1.4; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .de-file-name:hover { white-space: normal; word-break: break-word; }
  .de-file-actions { display: flex; gap: 4px; flex-shrink: 0; opacity: 0; transition: opacity 0.15s; }
  .de-file-row:hover .de-file-actions { opacity: 1; }
  .de-file-btn {
    display: flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border-radius: 7px; border: 1.5px solid;
    cursor: pointer; background: none; transition: all 0.15s;
  }
  .de-file-btn.dl { color: var(--accent-a); border-color: rgba(52,97,209,0.25); }
  .de-file-btn.dl:hover { background: rgba(52,97,209,0.08); border-color: rgba(52,97,209,0.5); }
  .de-file-btn.del { color: #D94040; border-color: rgba(217,64,64,0.2); }
  .de-file-btn.del:hover { background: rgba(217,64,64,0.07); border-color: rgba(217,64,64,0.45); }

  /* ── Empty / loading / error ── */
  .de-center { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; text-align: center; color: var(--text-3); gap: 12px; }
  .de-center-title { font-family: var(--font-head); font-size: 17px; font-weight: 600; color: var(--text-2); }
  .de-center-sub { font-size: 13px; max-width: 280px; line-height: 1.5; }
  .de-spinner { width: 36px; height: 36px; border-radius: 50%; border: 2.5px solid #E5E1D8; border-top-color: var(--accent-a); animation: de-spin 0.75s linear infinite; }
  @keyframes de-spin { to { transform: rotate(360deg); } }
  .de-error { display: flex; align-items: center; gap: 12px; padding: 16px 18px; border-radius: 10px; background: #FEF2F2; border: 1.5px solid #FECACA; color: #B91C1C; font-size: 14px; margin-bottom: 24px; }

  /* ── Modal ── */
  .de-backdrop { position: fixed; inset: 0; z-index: 9999; background: rgba(28,25,23,0.45); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 24px; }
  .de-modal { background: var(--surface); border-radius: 18px; padding: 28px; max-width: 420px; width: 100%; box-shadow: var(--shadow-lg); border: 1.5px solid var(--border); }
  .de-modal-icon { width: 48px; height: 48px; border-radius: 12px; background: #FEF2F2; border: 1.5px solid #FECACA; display: flex; align-items: center; justify-content: center; color: #D94040; margin-bottom: 18px; }
  .de-modal-title { font-family: var(--font-head); font-size: 18px; font-weight: 700; color: var(--text-1); margin-bottom: 8px; }
  .de-modal-desc { font-size: 14px; color: var(--text-2); line-height: 1.6; margin-bottom: 14px; }
  .de-modal-file { background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px; padding: 10px 14px; font-size: 13px; color: var(--text-1); word-break: break-all; margin-bottom: 22px; line-height: 1.5; }
  .de-modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
  .de-btn-cancel { padding: 9px 18px; border-radius: 9px; border: 1.5px solid var(--border); background: var(--surface); color: var(--text-2); font-family: var(--font-body); font-size: 13px; cursor: pointer; transition: all 0.15s; }
  .de-btn-cancel:hover { border-color: var(--text-2); color: var(--text-1); }
  .de-btn-del { padding: 9px 18px; border-radius: 9px; border: 1.5px solid #FECACA; background: #FEF2F2; color: #D94040; font-family: var(--font-body); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
  .de-btn-del:hover { background: #D94040; color: #fff; border-color: #D94040; }
  .de-btn-del:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Folder Card ──────────────────────────────────────────────────────────────
const FolderCard = ({ folderId, files, supplier, sectionKey, accentColor, searchQuery, canDelete, onDownload, onDeleteClick, forceOpen }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen !== undefined) setOpen(forceOpen);
  }, [forceOpen]);

  useEffect(() => {
    if (forceOpen !== undefined) return; 
    if (searchQuery && files.length > 0) setOpen(true);
    if (!searchQuery) setOpen(false);
  }, [searchQuery]);


  const bg  = accentColor + "18";
  const cnt = files.length;

  return (
    <div className={`de-folder ${open ? "open" : ""}`}>
      <div className="de-folder-header" onClick={() => setOpen(o => !o)}>
        <span className="de-folder-icon" style={{ color: accentColor }}>
          {open ? <Icons.FolderOpen /> : <Icons.Folder />}
        </span>
        <span className="de-folder-name" title={folderId}>{folderId}</span>
        <span className="de-folder-count" style={{ background: bg, color: accentColor }}>
          {cnt} {cnt === 1 ? "file" : "files"}
        </span>
        <span className="de-folder-chevron"><Icons.Chevron open={open} /></span>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="de-file-list"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: "hidden" }}
          >
            {files.map((fileName) => {
              const ext = getExt(fileName);
              const clr = extColor(ext);
              const display = fileName.length > 52 ? fileName.slice(0, 49) + "…" : fileName;
              return (
                <div
                  key={fileName}
                  className="de-file-row"
                  onClick={() => onDownload(fileName, supplier, folderId, sectionKey)}
                  title={fileName}
                >
                  <span className="de-file-ext" style={{ background: clr + "18", color: clr }}>
                    {ext.toUpperCase().slice(0, 4)}
                  </span>
                  <span className="de-file-name">
                    {highlight(display, searchQuery)}
                  </span>
                  <div className="de-file-actions" onClick={e => e.stopPropagation()}>
                    <button className="de-file-btn dl" title="Download" onClick={() => onDownload(fileName, supplier, folderId, sectionKey)}>
                      <Icons.Download />
                    </button>
                    {canDelete && (
                      <button className="de-file-btn del" title="Delete" onClick={() => onDeleteClick({ fileName, supplier, folderId, sectionKey })}>
                        <Icons.Trash />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const DocumentExplorer = () => {
  const { keycloak } = useKeycloak();
  const [documents, setDocuments]     = useState({ mspl_documents: {}, pdi_witness: {} });
  const [filtered, setFiltered]       = useState({ mspl_documents: {}, pdi_witness: {} });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab]     = useState("all");
  const [allOpen, setAllOpen]         = useState(undefined);
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting]   = useState(false);
  const searchRef = useRef(null);

  // Roles from Keycloak token directly
  const clientRoles = keycloak?.tokenParsed?.resource_access?.Redoms?.roles || [];
  const realmRoles  = keycloak?.tokenParsed?.realm_access?.roles || [];
  const canDelete   = [...new Set([...clientRoles, ...realmRoles])].includes("PDI_Supported_Documents_Delete");

  const projectId = localStorage.getItem("selectedProject");

  // ── Fetch ──
  const fetchDocuments = async () => {
    if (!projectId) { setError("No project selected."); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await apiClient.post("get_supported_document", { projectId });
      const docs = res.data.documents_by_supplier || { mspl_documents: {}, pdi_witness: {} };
      setDocuments(docs); setFiltered(docs);
    } catch { setError("Failed to load documents. Please try again."); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchDocuments(); }, []);

  // ── Search ──
  const debouncedSearch = debounce((v) => setSearchQuery(v), 250);

  useEffect(() => {
    if (!searchQuery.trim()) { setFiltered(documents); return; }
    const filterSec = (sec) => {
      const out = {};
      Object.entries(sec).forEach(([sup, folders]) => {
        const ff = {};
        Object.entries(folders).forEach(([fid, files]) => {
          const m = files.filter(f => f.toLowerCase().includes(searchQuery.toLowerCase()));
          if (m.length) ff[fid] = m;
        });
        if (Object.keys(ff).length) out[sup] = ff;
      });
      return out;
    };
    setFiltered({ mspl_documents: filterSec(documents.mspl_documents), pdi_witness: filterSec(documents.pdi_witness) });
  }, [searchQuery, documents]);

  // ── Download ──
  const downloadFile = async (fileName, supplier, folderId, sectionKey) => {
    const safe = fileName.replace(/[^a-zA-Z0-9_\-. ]/g, "");
    if (!safe || !projectId) return;
    try {
      const res = await apiClient.post("download_supported_document",
        { projectId, 
          fileName: safe, 
          supplierName: supplier, 
          folderName: folderId,
          document_type: sectionKey,
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = safe;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { console.error("Download failed"); }
  };

  // ── Delete ──
  const confirmDelete = async () => {
    if (!deleteModal || isDeleting) return;
    setIsDeleting(true);
    try {
      await apiClient.post("delete_supported_documents", {
        project_id: projectId, supplier: deleteModal.supplier,
        document_name: deleteModal.fileName, section: deleteModal.sectionKey, folder: deleteModal.folderId,
      });
      fetchDocuments(); setDeleteModal(null);
    } catch { alert("Delete failed. Please try again."); }
    finally { setIsDeleting(false); }
  };

  // ── Flatten to folder list ──
  const toFolderList = (secData, sectionKey) => {
    const list = [];
    Object.entries(secData || {}).forEach(([supplier, folders]) => {
      Object.entries(folders).forEach(([folderId, files]) => {
        if (files.length) list.push({ supplier, folderId, files, sectionKey });
      });
    });
    return list.sort((a, b) => a.folderId.localeCompare(b.folderId));
  };

  const pdiFolders  = toFolderList(filtered.pdi_witness,    "pdi_witness");
  const msplFolders = toFolderList(filtered.mspl_documents, "mspl_documents");
  const allFolders  = [...pdiFolders, ...msplFolders];

  const pdiFileCount  = pdiFolders.reduce((s, f) => s + f.files.length, 0);
  const msplFileCount = msplFolders.reduce((s, f) => s + f.files.length, 0);
  const totalFiles    = pdiFileCount + msplFileCount;

  const showPdi  = (activeTab === "all" || activeTab === "pdi")  && pdiFolders.length > 0;
  const showMspl = (activeTab === "all" || activeTab === "mspl") && msplFolders.length > 0;
  const isEmpty  = !showPdi && !showMspl && !isLoading && !error;

  // ── Section renderer ──
  const SectionCluster = ({ label, folders, accentColor, sectionClass }) => (
    <div className="de-section">
      <div className="de-section-label" style={{ color: accentColor }}>
        <span>{label}</span>
        <span style={{ fontWeight: 400, color: "var(--text-3)", fontSize: 12 }}>
          — {folders.length} folder{folders.length !== 1 ? "s" : ""}, {folders.reduce((s,f)=>s+f.files.length,0)} files
        </span>
        <div className="de-section-line" style={{ background: accentColor + "22" }} />
      </div>
      <div className="de-folder-grid">
        {folders.map(({ folderId, files, supplier, sectionKey }) => (
          <FolderCard
            key={`${supplier}-${folderId}`}
            folderId={folderId}
            files={files}
            supplier={supplier}
            sectionKey={sectionKey}
            accentColor={accentColor}
            searchQuery={searchQuery}
            canDelete={canDelete}
            onDownload={downloadFile}
            onDeleteClick={setDeleteModal}
            forceOpen={allOpen}
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <div className="de-root">

        {/* ── Header ── */}
        <motion.div className="de-header" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <span className="de-page-label">Support Documents</span>
          <h1 className="de-page-title">Document Vault</h1>
        </motion.div>

        {/* ── Stats pills ── */}
        {!isLoading && !error && (
          <motion.div className="de-stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <span className="de-stat-pill" style={{ background: "#EFF3FF", borderColor: "#C7D4F8", color: "#3461D1" }}>
              {totalFiles} total files
            </span>
            <span className="de-stat-pill" style={{ background: "#EFFAF6", borderColor: "#B4E8D6", color: "#1A9A78" }}>
              {pdiFileCount} PDI
            </span>
            <span className="de-stat-pill" style={{ background: "#FFF7ED", borderColor: "#FDD9AA", color: "#C4730A" }}>
              {msplFileCount} MSPL
            </span>
          </motion.div>
        )}

        {/* ── Toolbar ── */}
        <div className="de-toolbar">
          <div className="de-search">
            <span className="de-search-icon"><Icons.Search /></span>
            <input
              ref={searchRef}
              className="de-search-input"
              type="text"
              placeholder="Search files..."
              onChange={e => debouncedSearch(e.target.value)}
            />
            {searchQuery && (
              <button className="de-search-clear" onClick={() => { setSearchQuery(""); if (searchRef.current) searchRef.current.value = ""; }}>
                <Icons.X />
              </button>
            )}
          </div>

          <div className="de-tabs">
            {[
              { id: "all",  label: "All" },
              { id: "pdi",  label: "PDI" },
              { id: "mspl", label: "MSPL" },
            ].map(t => (
              <button key={t.id} className={`de-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          <button className="de-expand-btn" onClick={() => setAllOpen(o => !o)}>
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>

        {/* ── Error ── */}
        {error && <div className="de-error"><Icons.Warn />{error}</div>}

        {/* ── Loading ── */}
        {isLoading && (
          <div className="de-center">
            <div className="de-spinner" />
            <span className="de-center-sub">Loading documents…</span>
          </div>
        )}

        {/* ── Content ── */}
        {!isLoading && !error && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            {showPdi && (
              <SectionCluster
                label="PDI Review · TPI Documents"
                folders={pdiFolders}
                accentColor="#3461D1"
                sectionClass="pdi"
              />
            )}
            {showMspl && (
              <SectionCluster
                label="MSPL · Supported Documents"
                folders={msplFolders}
                accentColor="#1A9A78"
                sectionClass="mspl"
              />
            )}
            {isEmpty && (
              <div className="de-center">
                <Icons.Empty />
                <div className="de-center-title">{searchQuery ? "No matching files" : "No documents yet"}</div>
                <p className="de-center-sub">{searchQuery ? "Try a different search term." : "This project has no documents available."}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Delete Modal ── */}
        <AnimatePresence>
          {deleteModal && (
            <motion.div className="de-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => !isDeleting && setDeleteModal(null)}>
              <motion.div className="de-modal"
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 12 }}
                transition={{ duration: 0.18 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="de-modal-icon"><Icons.Warn /></div>
                <div className="de-modal-title">Delete this file?</div>
                <p className="de-modal-desc">
                  Permanently removes the file from the <strong>{deleteModal.folderId}</strong> folder. This cannot be undone.
                </p>
                <div className="de-modal-file">{deleteModal.fileName}</div>
                <div className="de-modal-actions">
                  <button className="de-btn-cancel" onClick={() => setDeleteModal(null)} disabled={isDeleting}>Cancel</button>
                  <button className="de-btn-del" onClick={confirmDelete} disabled={isDeleting}>
                    {isDeleting ? "Deleting…" : "Delete File"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default DocumentExplorer;


// import React, { useState, useEffect } from "react";
// import { FaFolder, FaFileDownload, FaSearch, FaExclamationCircle, FaTrash } from "react-icons/fa";
// import { motion } from "framer-motion";
// import debounce from "lodash/debounce";
// import apiClient from "../../pages/keycloak/axiosInstance";

// const DocumentExplorer = () => {
//   const [documents, setDocuments] = useState({ mspl_documents: {}, pdi_witness: {} });
//   const [filteredDocuments, setFilteredDocuments] = useState({ mspl_documents: {}, pdi_witness: {} });
//   const [searchQuery, setSearchQuery] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Delete modal states
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [deleteInfo, setDeleteInfo] = useState(null);
//   const [isDeleting, setIsDeleting] = useState(false);

//   // Role check
//   const userRoles = JSON.parse(sessionStorage.getItem("userRoles") || "[]");
//   const canDelete = userRoles.includes("PDI_Supported_Documents_Delete");

//   const projectId = localStorage.getItem("selectedProject");

//   const sectionTitles = {
//     pdi_witness: "PDI Review TPI Documents",
//     mspl_documents: "MSPL Supported Documents",
//   };

//   const fetchDocuments = async () => {
//     if (!projectId) {
//       setError("No project selected.");
//       return;
//     }
//     setIsLoading(true);
//     setError(null);
//     try {
//       const response = await apiClient.post(`get_supported_document`, { projectId });
//       const docs = response.data.documents_by_supplier || { mspl_documents: {}, pdi_witness: {} };
//       setDocuments(docs);
//       setFilteredDocuments(docs);
//     } catch (err) {
//       setError("Failed to load documents. Please try again later.");
//       console.error("Error fetching documents:", err);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchDocuments();
//   }, []);

//   // Search filtering
//   useEffect(() => {
//     if (!searchQuery.trim()) {
//       setFilteredDocuments(documents);
//       return;
//     }

//     const filterSection = (section) => {
//       const filtered = {};
//       Object.entries(section).forEach(([supplier, folders]) => {
//         const filteredFolders = {};
//         Object.entries(folders).forEach(([folderId, files]) => {
//           const matchingFiles = files.filter((file) =>
//             file.toLowerCase().includes(searchQuery.toLowerCase())
//           );
//           if (matchingFiles.length > 0) {
//             filteredFolders[folderId] = matchingFiles;
//           }
//         });
//         if (Object.keys(filteredFolders).length > 0) {
//           filtered[supplier] = filteredFolders;
//         }
//       });
//       return filtered;
//     };

//     setFilteredDocuments({
//       mspl_documents: filterSection(documents.mspl_documents),
//       pdi_witness: filterSection(documents.pdi_witness),
//     });
//   }, [searchQuery, documents]);

//   const downloadFile = async (filename, supplier, folderId) => {
//     const sanitizedFilename = filename.replace(/[^a-zA-Z0-9_\-. ]/g, "");
//     if (!sanitizedFilename || !projectId) return;

//     try {
//       const response = await apiClient.post(
//         `download_supported_document`,
//         { projectId,
//           fileName: sanitizedFilename,
//           supplierName: supplier,
//           folderName: folderId
//         },
//         { responseType: "blob" }
//       );

//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement("a");
//       link.href = url;
//       link.download = sanitizedFilename;
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       console.error("Download failed:", error);
//     }
//   };

//   const handleSearch = debounce((value) => {
//     setSearchQuery(value);
//   }, 300);

//   const highlightText = (text, query) => {
//     if (!query) return text;
//     const parts = text.split(new RegExp(`(${query})`, "gi"));
//     return parts.map((part, i) =>
//       part.toLowerCase() === query.toLowerCase() ? (
//         <mark key={i} className="bg-yellow-300 px-1 rounded">
//           {part}
//         </mark>
//       ) : (
//         part
//       )
//     );
//   };

//   const openDeleteModal = (sectionKey, supplier, folderId, fileName) => {
//     setDeleteInfo({ sectionKey, supplier, folderId, fileName });
//     setShowDeleteModal(true);
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setDeleteInfo(null);
//   };

//   const confirmDelete = async () => {
//     if (!deleteInfo || isDeleting) return;
//     setIsDeleting(true);

//     try {
//       await apiClient.post(`delete_supported_documents`, {
//         project_id: projectId,
//         supplier: deleteInfo.supplier,
//         document_name: deleteInfo.fileName,
//         section: deleteInfo.sectionKey,
//         folder: deleteInfo.folderId,
//       });
//       fetchDocuments();
//       cancelDelete();
//     } catch (error) {
//       console.error("Delete failed:", error);
//       alert("Failed to delete the file. Please try again.");
//     } finally {
//       setIsDeleting(false);
//     }
//   };

//   const FileRow = ({ fileName, sectionKey, supplier, folderId }) => {
//     const displayName = fileName.length > 80 ? `${fileName.slice(0, 77)}...` : fileName;

//     return (
//       <motion.div
//         initial={{ opacity: 0, x: -20 }}
//         animate={{ opacity: 1, x: 0 }}
//         transition={{ duration: 0.3 }}
//         className="group flex items-center justify-between py-4 px-6 rounded-xl bg-gray-50 hover:bg-indigo-50 transition-all cursor-pointer border border-transparent hover:border-indigo-200"
//         onClick={() => downloadFile(fileName, supplier, folderId)}
//         title="Click to download"
//       >
//         <div className="flex items-center flex-1 min-w-0">
//           <FaFileDownload className="mr-5 text-indigo-600 text-xl flex-shrink-0" />
//           <span className="text-gray-800 font-medium truncate" title={fileName}>
//             {highlightText(displayName, searchQuery)}
//           </span>
//         </div>

//         <div className="flex items-center space-x-6 opacity-0 group-hover:opacity-100 transition-opacity">
//           {/* <FaFileDownload className="text-indigo-600 text-xl hover:text-indigo-800" title="Download" /> */}
//           {canDelete && (
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 openDeleteModal(sectionKey, supplier, folderId, fileName);
//               }}
//               title="Delete file"
//             >
//               <FaTrash className="text-red-600 text-xl hover:text-red-800" />
//             </button>
//           )}
//         </div>
//       </motion.div>
//     );
//   };

//   const FolderGroup = ({ sectionKey, supplier, folderId, files }) => (
//     <div className="bg-white rounded-2xl border border-gray-300 overflow-hidden w-full">
//       <div className="px-2 py-2 bg-gray-100 flex items-center justify-between">
//         <div className="flex items-center">
//           <FaFolder className="mr-4 text-yellow-600 text-2xl" />
//           <span className="font-bold text-lg text-gray-800">{folderId}</span>
//         </div>
//         <span className="flex text-center text-sm text-gray-600">
//           {files.length} file{files.length !== 1 ? "s" : ""}
//         </span>
//       </div>

//       <div className="p-6 space-y-2 bg-gray-50">
//         {files.length > 0 ? (
//           files.map((file) => (
//             <FileRow
//               key={file}
//               fileName={file}
//               sectionKey={sectionKey}
//               supplier={supplier}
//               folderId={folderId}
//             />
//           ))
//         ) : (
//           <p className="text-center text-gray-400 py-8">No files in this folder</p>
//         )}
//       </div>
//     </div>
//   );

//   const renderDocumentsSection = (title, sectionKey, documentData) => {
//     const hasData = Object.keys(documentData).length > 0;
//     if (!hasData) return null;

//     // Flatten and sort folders across all suppliers
//     const folderList = [];
//     Object.entries(documentData).forEach(([supplier, foldersObj]) => {
//       Object.entries(foldersObj).forEach(([folderId, files]) => {
//         if (files.length > 0) {
//           folderList.push({ supplier, folderId, files });
//         }
//       });
//     });

//     // Sort folders alphabetically
//     folderList.sort((a, b) => a.folderId.localeCompare(b.folderId));

//     return (
//       <motion.section
//         initial={{ opacity: 0, y: 30 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5 }}
//         className="mb-20"
//       >
//         <h2 className="text-3xl font-bold text-gray-800 mb-12 flex items-center">
//           <span className="mr-4 text-4xl">📄</span>
//           {title}
//         </h2>

//         <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
//           {folderList.map(({ supplier, folderId, files }) => (
//             <FolderGroup
//               key={`${supplier}-${folderId}`}
//               sectionKey={sectionKey}
//               supplier={supplier}
//               folderId={folderId}
//               files={files}
//             />
//           ))}
//         </div>
//       </motion.section>
//     );
//   };

//   const totalFiles = Object.values(filteredDocuments).reduce(
//     (acc, section) =>
//       acc +
//       Object.values(section).reduce(
//         (sum, folders) => sum + Object.values(folders).flat().length,
//         0
//       ),
//     0
//   );

//   return (
//     <div className="min-h-screen bg-gray-100 py-10 px-2 sm:px-2 lg:px-2">
//       <div className="w-full mx-auto"> {/* Increased max width for better 2-column layout */}
//         {/* Search Bar */}
//         <div className="mb-14">
//           <div className="relative max-w-3xl mx-auto">
//             <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
//             <input
//               type="text"
//               placeholder="Search files by name..."
//               onChange={(e) => handleSearch(e.target.value)}
//               className="w-full pl-14 pr-12 py-5 rounded-3xl border-2 border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-100 text-lg shadow-xl transition-all"
//             />
//             {searchQuery && (
//               <button
//                 onClick={() => {
//                   setSearchQuery("");
//                   handleSearch("");
//                 }}
//                 className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-2xl"
//               >
//                 ×
//               </button>
//             )}
//           </div>
//           {searchQuery && (
//             <p className="text-center mt-4 text-gray-600">
//               Found <strong>{totalFiles}</strong> matching file{totalFiles !== 1 ? "s" : ""}
//             </p>
//           )}
//         </div>

//         {/* Loading & Error */}
//         {isLoading && (
//           <div className="flex flex-col items-center py-32">
//             <div className="animate-spin rounded-full h-20 w-20 border-4 border-indigo-200 border-t-indigo-600"></div>
//             <p className="mt-8 text-xl text-gray-600">Loading documents...</p>
//           </div>
//         )}

//         {error && (
//           <div className="bg-red-50 border-l-4 border-red-500 p-8 rounded-2xl flex items-center max-w-2xl mx-auto">
//             <FaExclamationCircle className="text-red-600 text-4xl mr-6" />
//             <div>
//               <p className="text-red-800 font-bold text-lg">Error</p>
//               <p className="text-red-700">{error}</p>
//             </div>
//           </div>
//         )}

//         {/* Content */}
//         {!isLoading && !error && (
//           <>
//             {renderDocumentsSection("PDI Review TPI Documents", "pdi_witness", filteredDocuments.pdi_witness)}
//             {renderDocumentsSection("MSPL Supported Documents", "mspl_documents", filteredDocuments.mspl_documents)}

//             {totalFiles === 0 && (
//               <div className="text-center py-32">
//                 <div className="text-8xl mb-8">📂</div>
//                 <h3 className="text-3xl font-bold text-gray-700 mb-4">
//                   {searchQuery ? "No matching files found" : "No documents available"}
//                 </h3>
//                 <p className="text-gray-500 text-lg max-w-md mx-auto">
//                   {searchQuery
//                     ? "Try a different search term or clear the search."
//                     : "This project has no supported documents yet."}
//                 </p>
//               </div>
//             )}
//           </>
//         )}
//       </div>

//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && deleteInfo && (
//         <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
//           <motion.div
//             initial={{ opacity: 0, scale: 0.95 }}
//             animate={{ opacity: 1, scale: 1 }}
//             className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full"
//           >
//             <div className="flex items-center mb-6">
//               <div className="text-red-600 text-5xl mr-5">⚠️</div>
//               <h2 className="text-3xl font-bold text-gray-800">Confirm File Deletion</h2>
//             </div>

//             <p className="text-gray-700 text-lg mb-4">
//               Are you sure you want to <span className="font-bold text-red-600">permanently delete</span> the file:
//             </p>
//             <p className="bg-gray-100 px-4 py-3 rounded-lg font-mono text-gray-900 mb-6">
//               {deleteInfo.fileName}
//             </p>

//             <p className="text-gray-700 mb-6">
//               Location: <span className="font-semibold">{deleteInfo.folderId}</span> folder —{" "}
//               {sectionTitles[deleteInfo.sectionKey]}
//             </p>

//             <p className="text-red-600 font-bold text-lg mb-8">
//               This action cannot be undone.
//             </p>

//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={cancelDelete}
//                 disabled={isDeleting}
//                 className="px-8 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 disabled={isDeleting}
//                 className="px-8 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition disabled:opacity-50 flex items-center"
//               >
//                 {isDeleting ? "Deleting..." : "Delete File"}
//               </button>
//             </div>
//           </motion.div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default DocumentExplorer;